import { mqMin, mqUser } from 'utils/responsive';
import styled from '@emotion/styled';
import { SubTitleLabel } from 'ui/components/elements/ColorLabel/SubTitleLabel';
import { ColorLabel } from 'ui/components/elements/ColorLabel/ColorLabel';
import { AGENT_TYPES, IPublicAgent } from 'domain/entities/Agent/Agent';

const PageWrapper = styled.div`
    width: 100%;
    min-height: 100svh;
    background: #f8f9fd;
    padding-top: 20px;
    padding-bottom: 20px;
`;

const Wrapper = styled.div`
    width: 100%;
    margin: 0 auto 100px auto;
    height: auto;
    background: #f8f9fd;
    box-shadow: 0px 8px 24px rgba(0, 0, 0, 0.12);
    border-radius: 16px;
    min-width: 320px;
    overflow: hidden;

    ${mqMin('NARROW')} {
        max-width: 1000px;
    }
`;

const HeaderSection = styled.div`
    position: relative;
    width: 100%;
    padding: 40px 30px 20px 30px;
    display: flex;
    gap: 24px;
    align-items: flex-start;

    ${mqUser('SP')} {
        flex-direction: column;
        padding: 30px 20px 50px 20px;
        gap: 20px;
    }
`;

const CompanyImageWrapper = styled.div`
    flex-shrink: 0;
    width: 120px;
    height: 120px;
    background-color: var(--white);
    border-radius: 12px;
    display: flex;
    align-items: center;
    justify-content: center;
    box-shadow: 0px 4px 12px rgba(0, 0, 0, 0.1);
    border: 3px solid #d3d3d3;
    overflow: hidden;
    position: relative;
    z-index: 1;
    margin-top: 0;

    ${mqUser('SP')} {
        width: 100px;
        height: 100px;
    }
`;

const CompanyImage = styled.img`
    width: 100%;
    height: 100%;
    object-fit: cover;
    display: block;
`;

const CompanyInfo = styled.div`
    flex: 1;
    display: flex;
    flex-direction: column;
    gap: 8px;
    position: relative;
    z-index: 1;
`;

const CompanyName = styled.div`
    font-size: var(--font-xxx-large);
    font-weight: 900;
    color: var(--dark-blue);
    line-height: 1.2;
    letter-spacing: -0.5px;
    margin-bottom: 5px;

    ${mqUser('SP')} {
        font-size: var(--font-x-large);
    }
`;

const ContentSection = styled.div`
    padding: 30px;
    display: flex;
    flex-direction: column;
    gap: 20px;

    ${mqUser('SP')} {
        padding: 20px;
    }
`;

const Item = styled.div`
    display: flex;
    gap: 12px;
    flex-direction: column;
    padding: 16px 20px;
    box-shadow: 0 1px 4px rgba(0, 0, 0, 0.06);
`;

const Title = styled.div`
    font-size: var(--font-medium);
    font-weight: 700;
    color: var(--dark-blue);
    letter-spacing: 0.3px;
`;

const TextArea = styled.div`
    font-size: var(--font-large);
    padding: 16px 20px;
    box-shadow: 0 1px 4px rgba(0, 0, 0, 0.06);
    color: var(--dark-blue);
    line-height: 1.8;

    p {
        line-height: 1.8;
        margin: 0;
    }
`;

const GrayLabel = styled.div`
    border-radius: 4px;
    background-color: #f3f3f3;
    align-items: center;
    font-size: var(--font-medium);
    color: #222222;
    font-weight: 500;
    padding: 0.2rem 0.9rem;
`;

const IndustryLabelContainer = styled.div`
    display: flex;
    flex-wrap: wrap;
    gap: 8px;
    align-items: center;
`;

const AreaLabelContainer = styled.div`
    display: flex;
    flex-wrap: wrap;
    gap: 8px;
    align-items: center;
`;

const LabelsContainer = styled.div`
    display: flex;
    flex-wrap: wrap;
    gap: 8px;
    align-items: center;
`;

const Balloon = styled.div`
    font-size: var(--font-medium);
    font-weight: var(--font-bold);
    margin: 0 30px;
    background-color: #fffff9;
    padding: 15px;
    border-radius: 10px;
    position: relative;
    box-shadow: 0 4px 8px rgba(0, 0, 0, 0.1), 0 2px 4px rgba(0, 0, 0, 0.06);
    line-height: 1.5;
    white-space: pre-wrap;

    &::before {
        content: '';
        position: absolute;
        top: -12px;
        left: 20px;
        width: 0;
        height: 0;
        border-left: 12px solid transparent;
        border-right: 12px solid transparent;
        border-bottom: 12px solid #fffff9;
    }
`;

export const AgentProfile: React.FC<{ agent: IPublicAgent }> = ({ agent }) => {
    if (!agent) return null;

    return (
        <PageWrapper>
            <Wrapper>
                <HeaderSection>
                    {agent.icon_image && (
                        <CompanyImageWrapper>
                            <CompanyImage src={agent.icon_image} alt={agent.name} />
                        </CompanyImageWrapper>
                    )}
                    <CompanyInfo>
                        <CompanyName>{agent.name}</CompanyName>
                        <LabelsContainer>
                            {agent.agent_type === AGENT_TYPES.AGENCY.value && (
                                <ColorLabel color="#ee7800" backgroundColor="#fafad2" borderColor="#ee7800">
                                    {AGENT_TYPES.AGENCY.label}
                                </ColorLabel>
                            )}
                            {agent.agent_type === AGENT_TYPES.FA.value && (
                                <ColorLabel color="#006eb0" backgroundColor="#e5ffff" borderColor="#006eb0">
                                    {AGENT_TYPES.FA.label}
                                </ColorLabel>
                            )}
                            {agent.agent_type === AGENT_TYPES.BUSINESS_ACQUISITION.value && (
                                <ColorLabel color="#56256e" backgroundColor="#efefff" borderColor="#56256e">
                                    {AGENT_TYPES.BUSINESS_ACQUISITION.label}
                                </ColorLabel>
                            )}
                            {agent.is_sole_proprietor && (
                                <ColorLabel color="#008000" backgroundColor="#d3ffa8" borderColor="#008000">
                                    個人事業主
                                </ColorLabel>
                            )}
                        </LabelsContainer>
                    </CompanyInfo>
                </HeaderSection>

                {agent.business_description && <Balloon>{agent.business_description}</Balloon>}

                <ContentSection>
                    <SubTitleLabel>この企業について</SubTitleLabel>
                    <Item>
                        <Title>対応可能な業種</Title>
                        <IndustryLabelContainer>
                            {Array.isArray(agent.industries_served)
                                ? agent.industries_served
                                      .filter((industry: string) => industry && industry.trim() !== '')
                                      .map((industry: string) => <GrayLabel key={industry}>{industry}</GrayLabel>)
                                : agent.industries_served && <GrayLabel>{agent.industries_served}</GrayLabel>}
                        </IndustryLabelContainer>
                    </Item>

                    {agent.regions_served && (
                        <Item>
                            <Title>対応可能なエリア</Title>
                            <AreaLabelContainer>
                                {Array.isArray(agent.regions_served) &&
                                    agent.regions_served
                                        .filter((area: string) => area && area.trim() !== '')
                                        .map((area: string) => <GrayLabel key={area}>{area}</GrayLabel>)}
                            </AreaLabelContainer>
                        </Item>
                    )}
                </ContentSection>
            </Wrapper>
        </PageWrapper>
    );
};
